import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { Field } from 'formik';
import Search from '@mui/icons-material/Search';
import { InputAdornment, MenuItem, Popper } from '@mui/material';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import TextField from '../TextField';
import AppCircularProgress from '../../AppCircularProgress/AppCircularProgress';
import styles from './SearchField.module.scss';

const SearchField = ({
  name,
  label,
  options,
  onInputTextChange,
  onChange,
  onFocus,
  loading,
  className,
  placeholder,
  linkTypeOptions,
  setField,
  onEnter,
}) => {
  const SearchRef = useRef();

  const [anchorEl, setAnchorEl] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isMenuOpened, setMenuOpened] = useState(false);

  useEffect(() => {
    setAnchorEl(get(SearchRef, 'current'));
  }, []);

  useEffect(() => {
    if (!loading && get(options, 'length', []) > 0 && isOpen) {
      setMenuOpened(true);
    }
  }, [loading, options]);

  const onInputChange = event => {
    const { value } = event.target;
    setField(value);
    onInputTextChange(value);
    if (!value) {
      setIsOpen(false);
      setMenuOpened(false);
    } else {
      setIsOpen(true);
      setMenuOpened(true);
    }
  };

  const handleClose = () => setMenuOpened(false);

  const onInputFocus = event => {
    setIsOpen(true);
    const { value } = event.target;
    setField(value);
    if (onFocus) {
      onFocus(value);
    }
  };
  const onInputBlur = () => {
    setIsOpen(false);
  };

  return (
    <>
      <Field
        className={className}
        key={name}
        id={name}
        name={name}
        label={label}
        placeholder={placeholder}
        component={TextField}
        onChange={onInputChange}
        onFocus={onInputFocus}
        onBlur={onInputBlur}
        onKeyDown={e => {
          if (e.keyCode === 13) {
            // Enter
            e.preventDefault();
            document.getElementById(name).blur();
            setMenuOpened(false);
            if (onEnter) {
              onEnter();
            }
          }
        }}
        inputProps={{
          // change loading indicator
          endAdornment: (
            <InputAdornment position="end">
              {loading && <AppCircularProgress className={styles.loadingIcon} />}
              <Search className={styles.endAdornment} />
            </InputAdornment>
          ),
          ref: SearchRef,
        }}
      />
      {isMenuOpened && (
        <ClickAwayListener onClickAway={handleClose}>
          <Popper
            className={styles.menu}
            anchorEl={anchorEl}
            open={isMenuOpened}
            style={{ width: get(SearchRef, 'current.clientWidth') }}
          >
            {options.map(option => {
              const optionValue = get(option, 'value', '');
              return (
                <MenuItem
                  key={`${name}-${optionValue}`}
                  onClick={() => {
                    onChange(option);
                    setMenuOpened(false);
                  }}
                  style={{ whiteSpace: 'normal' }}
                >
                  {linkTypeOptions ? (
                    <span className={styles.linkOption}>{get(option, 'label', '')}</span>
                  ) : (
                    get(option, 'label', '')
                  )}
                </MenuItem>
              );
            })}
          </Popper>
        </ClickAwayListener>
      )}
    </>
  );
};

SearchField.propTypes = {
  className: PropTypes.string,
  name: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  options: PropTypes.array,
  onInputTextChange: PropTypes.func,
  onChange: PropTypes.func,
  loading: PropTypes.bool,
  linkTypeOptions: PropTypes.bool,
  setField: PropTypes.func,
  onEnter: PropTypes.func,
};

SearchField.defaultProps = {
  placeholder: '',
  onChange: () => {},
  setField: () => {},
  linkTypeOptions: true,
  loading: false,
  options: [],
};

export default SearchField;
